import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import { adminApi, ZigRate } from '../../api/admin';
import {
  downloadExcelBuffer,
  formatDateForExport,
  styleExcelHeaderRow,
  todayFileStamp,
  writeLandscapePdfTable,
} from '../../utils/adminReportExport';

const ZigRateManagement: React.FC = () => {
  const [rates, setRates] = useState<ZigRate[]>([]);
  const [activeRate, setActiveRate] = useState<ZigRate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = useState(false);
  const [editingRate, setEditingRate] = useState<ZigRate | null>(null);
  const [isHistoricalRate, setIsHistoricalRate] = useState(false);
  const [formData, setFormData] = useState({ rate: '', notes: '', effectiveDate: '' });
  const [bulkUploadText, setBulkUploadText] = useState('');

  useEffect(() => {
    loadRates();
  }, []);

  const loadRates = async () => {
    setLoading(true);
    setError('');
    try {
      const [allRates, active] = await Promise.all([
        adminApi.getAllZigRates(),
        adminApi.getActiveZigRate(),
      ]);
      setRates(allRates);
      setActiveRate(active);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load ZIG rates');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (rate?: ZigRate) => {
    if (rate) {
      setEditingRate(rate);
      setIsHistoricalRate(false); // Editing doesn't support historical dates
      setFormData({
        rate: rate.rate.toString(),
        notes: rate.notes || '',
        effectiveDate: '',
      });
    } else {
      setEditingRate(null);
      setIsHistoricalRate(false);
      setFormData({ rate: '', notes: '', effectiveDate: '' });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRate(null);
    setIsHistoricalRate(false);
    setFormData({ rate: '', notes: '', effectiveDate: '' });
  };

  const handleCloseBulkUploadDialog = () => {
    setBulkUploadDialogOpen(false);
    setBulkUploadText('');
  };

  const handleSubmit = async () => {
    if (!formData.rate || parseFloat(formData.rate) <= 0) {
      setError('Please enter a valid rate');
      return;
    }

    if (isHistoricalRate && !formData.effectiveDate) {
      setError('Please enter an effective date for historical rates');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (editingRate && editingRate.id) {
        await adminApi.updateZigRate(
          editingRate.id,
          parseFloat(formData.rate),
          formData.notes || undefined
        );
        setSuccess('ZIG rate updated successfully');
      } else if (isHistoricalRate) {
        // Create historical rate for specific date
        const dateStr = new Date(formData.effectiveDate).toISOString();
        await adminApi.createZigRateForDate(
          parseFloat(formData.rate),
          dateStr,
          formData.notes || undefined
        );
        setSuccess('Historical ZIG rate created successfully');
      } else {
        // Create current rate (deactivates existing rates)
        await adminApi.createZigRate(
          parseFloat(formData.rate),
          formData.notes || undefined
        );
        setSuccess('ZIG rate created successfully');
      }
      handleCloseDialog();
      loadRates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save ZIG rate');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkUploadText.trim()) {
      setError('Please enter rate data');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Parse CSV format: date,rate,notes (optional)
      const lines = bulkUploadText.trim().split('\n');
      const rates: Array<{ rate: number; date: string; notes?: string }> = [];

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        const parts = line.split(',').map(p => p.trim());
        if (parts.length < 2) {
          throw new Error(`Line ${i + 1}: Invalid format. Expected: date,rate[,notes]`);
        }

        const dateStr = parts[0];
        const rate = parseFloat(parts[1]);
        const notes = parts[2] || undefined;

        if (isNaN(rate) || rate <= 0) {
          throw new Error(`Line ${i + 1}: Invalid rate value`);
        }

        // Validate date format (YYYY-MM-DD or ISO format)
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) {
          throw new Error(`Line ${i + 1}: Invalid date format. Use YYYY-MM-DD`);
        }

        rates.push({
          date: date.toISOString(),
          rate,
          notes,
        });
      }

      if (rates.length === 0) {
        throw new Error('No valid rates found');
      }

      const result = await adminApi.bulkUploadZigRates(rates);
      setSuccess(`Successfully uploaded ${result.count} ZIG rates`);
      handleCloseBulkUploadDialog();
      loadRates();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err: any) {
      setError(err.message || err.response?.data?.error || 'Failed to bulk upload ZIG rates');
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = () => {
    writeLandscapePdfTable({
      title: 'ZIG Rates Report',
      subtitle: activeRate
        ? `Active rate: 1 USD = ${activeRate.rate} ZIG`
        : 'No active rate',
      headers: ['ID', 'Rate', 'Effective Date', 'Active', 'Created By', 'Notes'],
      colWidths: [20, 30, 45, 25, 40, 80],
      rows: rates.map((rate) => [
        String(rate.id ?? 'N/A'),
        String(rate.rate),
        formatDateForExport(rate.effectiveDate),
        rate.isActive ? 'Yes' : 'No',
        rate.createdBy || 'N/A',
        rate.notes || '',
      ]),
      fileName: `zig-rates-report-${todayFileStamp()}.pdf`,
    });
  };

  const exportToExcel = async () => {
    try {
      const ExcelJS = await import('exceljs');
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('ZIG Rates');

      worksheet.columns = [
        { header: 'ID', key: 'id', width: 10 },
        { header: 'Rate', key: 'rate', width: 14 },
        { header: 'Effective Date', key: 'effectiveDate', width: 22 },
        { header: 'Active', key: 'isActive', width: 10 },
        { header: 'Created By', key: 'createdBy', width: 24 },
        { header: 'Updated By', key: 'updatedBy', width: 24 },
        { header: 'Notes', key: 'notes', width: 40 },
      ];
      styleExcelHeaderRow(worksheet);

      rates.forEach((rate) => {
        worksheet.addRow({
          id: rate.id ?? '',
          rate: rate.rate,
          effectiveDate: formatDateForExport(rate.effectiveDate),
          isActive: rate.isActive ? 'Yes' : 'No',
          createdBy: rate.createdBy || '',
          updatedBy: rate.updatedBy || '',
          notes: rate.notes || '',
        });
      });

      const metadataSheet = workbook.addWorksheet('Export Info');
      metadataSheet.columns = [
        { header: 'Property', key: 'property', width: 25 },
        { header: 'Value', key: 'value', width: 50 },
      ];
      styleExcelHeaderRow(metadataSheet);
      metadataSheet.addRows([
        { property: 'Export Date', value: new Date().toLocaleString() },
        { property: 'Total Rates', value: rates.length },
        {
          property: 'Active Rate',
          value: activeRate ? `1 USD = ${activeRate.rate} ZIG` : 'None',
        },
        {
          property: 'Active Effective Date',
          value: activeRate?.effectiveDate
            ? formatDateForExport(activeRate.effectiveDate)
            : 'N/A',
        },
      ]);

      const buffer = await workbook.xlsx.writeBuffer();
      await downloadExcelBuffer(buffer, `zig-rates-report-${todayFileStamp()}.xlsx`);
    } catch (err) {
      console.error('Failed to export Excel:', err);
      alert('Failed to export Excel. Please ensure exceljs is installed.');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5">ZIG Rate Management</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={exportToPDF}
            disabled={loading || rates.length === 0}
            color="error"
          >
            Export PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<TableChartIcon />}
            onClick={exportToExcel}
            disabled={loading || rates.length === 0}
            color="success"
          >
            Export Excel
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => setBulkUploadDialogOpen(true)}
          >
            Bulk Upload
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Create New Rate
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {activeRate && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <Typography variant="h6" gutterBottom>
            Active ZIG Rate
          </Typography>
          <Typography variant="h4">1 USD = {activeRate.rate} ZIG</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Effective Date: {activeRate.effectiveDate ? new Date(activeRate.effectiveDate).toLocaleString() : 'N/A'}
          </Typography>
          {activeRate.notes && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Notes: {activeRate.notes}
            </Typography>
          )}
        </Paper>
      )}

      {loading && rates.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Rate (USD to ZIG)</TableCell>
                <TableCell>Effective Date</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rates.map((rate) => (
                <TableRow key={rate.id}>
                  <TableCell>
                    <Typography variant="h6">1 USD = {rate.rate} ZIG</Typography>
                  </TableCell>
                  <TableCell>
                    {rate.effectiveDate
                      ? new Date(rate.effectiveDate).toLocaleString()
                      : 'N/A'}
                  </TableCell>
                  <TableCell>{rate.createdBy || 'N/A'}</TableCell>
                  <TableCell>{rate.notes || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={rate.isActive ? 'Active' : 'Inactive'}
                      color={rate.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(rate)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRate ? 'Edit ZIG Rate' : 'Create New ZIG Rate'}
        </DialogTitle>
        <DialogContent>
          {!editingRate && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <label>
                <input
                  type="checkbox"
                  checked={isHistoricalRate}
                  onChange={(e) => setIsHistoricalRate(e.target.checked)}
                  style={{ marginRight: 8 }}
                />
                Create historical rate for a past date
              </label>
            </Box>
          )}
          {isHistoricalRate && !editingRate && (
            <TextField
              autoFocus
              margin="dense"
              label="Effective Date"
              type="date"
              fullWidth
              variant="outlined"
              value={formData.effectiveDate}
              onChange={(e) => setFormData({ ...formData, effectiveDate: e.target.value })}
              helperText="Select the date this rate was effective"
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 2 }}
            />
          )}
          <TextField
            autoFocus={!isHistoricalRate || editingRate !== null}
            margin="dense"
            label="Rate (USD to ZIG)"
            type="number"
            fullWidth
            variant="outlined"
            value={formData.rate}
            onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
            helperText="Enter the exchange rate (e.g., 13.5 means 1 USD = 13.5 ZIG)"
            sx={{ mt: 2 }}
          />
          <TextField
            margin="dense"
            label="Notes (Optional)"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : editingRate ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkUploadDialogOpen} onClose={handleCloseBulkUploadDialog} maxWidth="md" fullWidth>
        <DialogTitle>Bulk Upload ZIG Rates</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Enter rates in CSV format, one per line: <strong>date,rate,notes</strong>
            <br />
            Example:
            <br />
            <code>2024-01-01,13.5,Rate for January</code>
            <br />
            <code>2024-02-01,14.2,Rate for February</code>
            <br />
            <code>2024-03-01,15.0</code>
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Rate Data (CSV)"
            fullWidth
            multiline
            rows={10}
            variant="outlined"
            value={bulkUploadText}
            onChange={(e) => setBulkUploadText(e.target.value)}
            placeholder="2024-01-01,13.5,Rate for January&#10;2024-02-01,14.2,Rate for February&#10;2024-03-01,15.0"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkUploadDialog}>Cancel</Button>
          <Button onClick={handleBulkUpload} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ZigRateManagement;

